/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable react-refresh/only-export-components */
/* eslint-disable react-hooks/exhaustive-deps */
import { Form, redirect, useParams } from 'react-router-dom';
import { ChangeEvent, useEffect, useState } from 'react';
import SplitLayout from '../components/splitLayout';
import List from '../components/campaigns/list';
import LoadingModal from '../components/loadingModal';
import {
  CampaignStore, LetterTemplate, SMSSenderInformation, SMSTemplate,
} from '../types/campaigns.type';
import Modal from '../components/modal';
import customIcons from '../utils/icons';
import Steps from '../components/campaigns/steps';
import campaignSteps, { sendLetterStep } from '../utils/campaignSteps';
import useCampaignStore from '../components/campaigns/campaignStore';
import { formatSnapshot } from '../utils/formatters';
import { Channel, TemplateOption } from '../types/enums';
import SMS from '../components/campaign-confirmation/smsPreview';
import LetterPreview from '../components/campaign-confirmation/letterPreview';
import SMSGallery from '../components/campaign-confirmation/smsGallery';
import useRequestState from '../hooks/useRequestState';
import PostLetterTemplates from '../services/adapters/letterTemplate';
import GetSmsTemplates from '../services/adapters/smsTemplate';
import PostAddCampaign from '../services/adapters/addCampaign';
import Button from '../components/buttons/baseButton';
import useFilterStore from '../components/streetview/filterStore';
import UploadTemplate from '../components/form-inputs/uploadTemplate';
import useCampaignTemplateFileUpload from '../hooks/useCampaignTemplateFileUpload';
import UploadSmsTemplate from '../components/uploadSmsTemplate';

export async function action({ params }: { params: { campaignId: string, impactTheme: string } }) {
  const { campaignId } = params;
  const { selectedFilters } = useFilterStore.getState();
  const addCampaignAdapter = new PostAddCampaign();
  await addCampaignAdapter.adaptResponse({ id: campaignId, selectedFilters });
  return redirect('/campaigns');
}

export default function CampaignConfirmation() {
/*
campaigns = array of all campaigns store in state store
campaign data = ALL campaign info that is collected and generated (generated being the id and created at date)
campaign information = is the campaign info that is collected from the user
*/
  const [isModalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSms, setShowSms] = useState(false);
  const [smsText, setSmsText] = useState('');
  const [showLetter, setShowLetter] = useState(false);
  const [letterUrl, setLetterUrl] = useState('');
  const [displayValue, setDisplayValue] = useState('Please select a template');
  const displayExcludeHouseholds = false;
  const [templateOption, setTemplateOption] = useState<TemplateOption>(TemplateOption.UPLOAD);
  const [smsTemplateOption, setSmsTemplateOption] = useState<TemplateOption>(TemplateOption.UPLOAD);

  const params = useParams();
  const campaigns = useCampaignStore((state) => state.campaigns);
  const setTemplate = useCampaignStore((state) => state.setTemplate);
  const campaignData = campaigns.find((campaign) => campaign.id === params.campaignId) as CampaignStore;

  const {
    data: smsTemplates,
    handleRequest: handleSmsRequest,
    FallbackWrapper: FallbackSmsWrapper,
  } = useRequestState<SMSSenderInformation>();

  const {
    data: letterTemplates,
    handleRequest: handleLetterRequest,
    FallbackWrapper: FallbackLetterWrapper,
  } = useRequestState<LetterTemplate[]>();

  const {
    campaignName, campaignType, snapshot, excludeHouseholds, excludeHouseholdsByMonth, communicationChannel, stagger, dispatchDate,
  } = campaignData;

  const {
    templateFileName,
    templateIsUploading,
    templateUploadError,
    resetUpload,
    uploadFile,
  } = useCampaignTemplateFileUpload(campaignType, (s3Key: string) => {
    setTemplate(campaignData.id, s3Key);
  });

  const campaign = {
    'Campaign Name': campaignName,
    'Campaign Type': campaignType,
    'LIFT Snapshot': formatSnapshot(snapshot, 'long'),
  };
  const titleObject = {
    title: 'Campaign Manager',
    icon: customIcons.sendIcon,
  };

  const steps = [...campaignSteps, sendLetterStep];
  const modalMessage = `This campaign will be be sent to Policy in Practice and drafts generated for review.
  We will be in touch to confirm details of the campaign before it is sent to residents. Would you like to proceed?`;

  const handleModalOpen = () => {
    setModalOpen((previousState) => !previousState);
  };
  const handleLoading = () => {
    setLoading(true);
  };
  const excludeHouseholdsText = (numberOfMonths: string) => (
    <>
      Households contacted in the last
      <strong>{` ${numberOfMonths?.toLowerCase()}`}</strong>
      will be excluded`
    </>
  );
  const includeHouseholdsText = 'Included previously contacted households';

  const handleSms = (template: SMSTemplate) => {
    setTemplate(campaignData.id, template.id);
    setSmsText(template.template);
    setShowSms(true);
  };

  const handleSmsSubmitSuccess = (text: string, id: number) => {
    setTemplate(campaignData.id, id);
    setSmsText(text);
  };

  const handleLetter = (event: ChangeEvent<HTMLSelectElement>, campaignId: string) => {
    const template = letterTemplates?.find((t) => t.id === event.target.value);
    if (!template) return;
    setDisplayValue(template.displayText);
    setTemplate(campaignId, template.id);
    setLetterUrl(template.letterTemplateSignedUrl);
    setShowLetter(true);
  };

  useEffect(() => {
    if (!campaignData) return;
    const loadTemplates = async (commsChannel: string, benefitType: string) => {
      if (commsChannel === Channel.SMS) {
        const GetSmsTemplatesInstance = new GetSmsTemplates();
        const getSmsTemplates = GetSmsTemplatesInstance.adaptResponse();
        await handleSmsRequest(getSmsTemplates);
      }
      if (commsChannel === Channel.Letter) {
        const postLetterTemplateInstance = new PostLetterTemplates();
        const postLetterTemplates = postLetterTemplateInstance.adaptResponse(benefitType);
        await handleLetterRequest(postLetterTemplates);
      }
    };

    loadTemplates(communicationChannel, campaignType);
  }, [communicationChannel, campaignType]);

  // function to conditionally render content based on loading state and modals
  const renderContent = () => {
    if (isModalOpen) {
      if (loading) {
        return (<LoadingModal message="loading" />);
      }

      return (
        <Modal isOpen={isModalOpen} onClose={handleModalOpen}>
          <span className="text-6xl text-primary">{customIcons.mailWarningIcon}</span>
          <h2 className="text-xl font-bold text-primary-dark">Please Confirm</h2>
          <p className="font-thin whitespace-wrap">{modalMessage}</p>
          <div className="w-full flex justify-between p-2">
            <button
              type="button"
              id="cancelModalButton"
              className="p-1 w-1/2 m-2 shadow-inner bg-white rounded-md text-secondary border"
              onClick={handleModalOpen}
            >
              Cancel
            </button>
            <Form method="post" className="w-1/2" onSubmit={handleLoading} replace>
              <button
                type="submit"
                className="w-full p-1 m-2 bg-primary rounded-md text-white"
              >
                Send for Review
              </button>
            </Form>
          </div>
        </Modal>
      );
    } return (
      <>
        <h1 className="m-2 p-4 text-header font-medium text-primary-dark border-b-2">Preview Campaign</h1>
        <SplitLayout leftWeight="" rightWeight="">

          <Steps
            titleObject={titleObject}
            steps={steps}
            selected={{
              campaignName, campaignType, snapshot, excludeHouseholds, communicationChannel, stagger, dispatchDate,
            }}
          />

          <div className="flex flex-grow flex-col overflow-scroll">
            <List campaign={campaign} />
            {displayExcludeHouseholds && (
            <p className="text-sm text-center">
              {excludeHouseholds && excludeHouseholdsByMonth ? excludeHouseholdsText(excludeHouseholdsByMonth) : includeHouseholdsText}
            </p>
            )}
            <FallbackSmsWrapper loadingMessage="Loading sms template options">
              {communicationChannel === Channel.SMS && (
              <>
                <div id="sms-template-choice-container" className="flex flex-col p-2 justify-between font-thin w-full">
                  <span className="text-sm">Select SMS Template Option</span>

                  <label htmlFor="sms-template-create-option" className="flex items-center gap-2">
                    <input
                      id="sms-template-create-option"
                      type="radio"
                      name="smsTemplateOption"
                      value={TemplateOption.UPLOAD}
                      checked={smsTemplateOption === TemplateOption.UPLOAD}
                      onChange={() => setSmsTemplateOption(TemplateOption.UPLOAD)}
                    />
                    <span className="text-sm">Create a new SMS template</span>
                  </label>
                  <label htmlFor="sms-template-existing-option" className="flex items-center gap-2">
                    <input
                      id="sms-template-existing-option"
                      type="radio"
                      name="smsTemplateOption"
                      value={TemplateOption.EXISTING}
                      checked={smsTemplateOption === TemplateOption.EXISTING}
                      onChange={() => setSmsTemplateOption(TemplateOption.EXISTING)}
                    />
                    <span className="text-sm">Choose from existing templates</span>
                  </label>
                </div>

                {smsTemplateOption === TemplateOption.UPLOAD && (
                <div>
                  <UploadSmsTemplate
                    key={smsTemplateOption}
                    parentText={smsText}
                    smsSenderId={smsTemplates?.smsSenderId as string}
                    onSubmitSuccess={handleSmsSubmitSuccess}
                  />
                </div>
                )}

                {smsTemplateOption === TemplateOption.EXISTING && (
                <div>
                  <SMSGallery smsTemplateList={smsTemplates?.smsTemplates ?? []} handleSms={handleSms} />
                </div>
                )}

                {smsTemplateOption === TemplateOption.EXISTING && showSms && (
                <SMS sender={smsTemplates?.smsSenderId as string} message={smsText} />
                )}
              </>
              )}
            </FallbackSmsWrapper>

            <FallbackLetterWrapper loadingMessage="Loading template options">

              {communicationChannel === Channel.Letter && (
              <>
                <div id="file-upload-choice-container" className="flex flex-col p-2 justify-between font-thin w-full">
                  <span className="text-sm">Select Template Option</span>
                  <label htmlFor="letter-template-upload-option" className="flex items-center gap-2">
                    <input
                      id="letter-template-upload-option"
                      type="radio"
                      name="templateOption"
                      value={TemplateOption.UPLOAD}
                      checked={templateOption === TemplateOption.UPLOAD}
                      onChange={() => {
                        setTemplateOption(TemplateOption.UPLOAD);
                        setShowLetter(false);
                      }}
                    />
                    <span className="text-sm">Upload a new Word (.docx) document</span>
                  </label>
                  <label htmlFor="letter-template-existing-option" className="flex items-center gap-2">
                    <input
                      id="letter-template-existing-option"
                      type="radio"
                      name="templateOption"
                      value={TemplateOption.EXISTING}
                      checked={templateOption === TemplateOption.EXISTING}
                      onChange={() => setTemplateOption(TemplateOption.EXISTING)}
                    />
                    <span className="text-sm">Choose from existing templates</span>
                  </label>
                </div>

                {templateOption === TemplateOption.UPLOAD && (
                <div className="flex flex-col p-2 justify-between font-thin w-full">
                  <span className="text-sm">Upload New Campaign Template</span>
                  <UploadTemplate
                    uploadFile={uploadFile}
                    isUploading={templateIsUploading}
                    uploadedFileName={templateFileName}
                    error={templateUploadError}
                    reset={resetUpload}
                  />
                </div>
                )}

                {templateOption === TemplateOption.EXISTING && (
                <div>
                  <label htmlFor="letter-template-dropwdown" className="flex flex-col p-2 justify-between font-thin w-full">
                    <span className="text-sm">Choose Existing Letter Template</span>
                    <select
                      id="letter-template-dropdown"
                      value={displayValue}
                      name="letter-template-dropdown"
                      onChange={(e) => handleLetter(e, campaignData.id)}
                      className="flex-1 p-1 rounded-md align-middle bg-slate-50 border border-pipgrey-light text-piptext-body"
                    >
                      <option value="">{displayValue}</option>
                      {Array.isArray(letterTemplates) && letterTemplates.map((letterTemplate: LetterTemplate) => (
                        <option
                          key={letterTemplate.id}
                          value={letterTemplate.id}
                        >
                          {letterTemplate.displayText}
                        </option>

                      ))}
                    </select>
                  </label>
                </div>
                )}

              </>
              )}
            </FallbackLetterWrapper>

            {showLetter && communicationChannel === Channel.Letter ? <LetterPreview url={letterUrl} /> : ''}
          </div>
        </SplitLayout>
        <div className="w-full m-2 flex justify-center">
          {(campaignData.smsTemplateId || campaignData.letterTemplateId) ? (
            <Button
              id="send-campaign-for-review"
              className="button-green"
              label="Send for Review"
              onClick={handleModalOpen}
            />
          ) : <span>Please choose a template</span>}
        </div>
      </>

    );
  };
  return (
    <div className="h-screen flex flex-col w-full p-4">
      {renderContent()}
    </div>
  );
}
